'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA, BlogWhatTheAppChecks } from '@/app/components/blog/BlogAeo'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="PFAS in Dental Floss: Forever Chemicals in Oral-B Glide & More"
      author="MicroPlastics Editorial Team"
      date="October 28, 2025"
      readTime="9 min read"
      heroImage="/blog/app-tracking.png"
      heroImageAlt="PFAS in dental floss, forever chemicals study"
    >
      <BlogLastUpdated iso="2026-05-25" formatted="May 25, 2026" />

      <BlogTLDR>
        Mamavation&apos;s 2024 EPA-certified lab study tested <strong>39 dental
        floss products</strong> and found PFAS &ldquo;forever chemicals&rdquo;
        in roughly <strong>one third of samples</strong>. <strong>Oral-B Glide
        tested at 248,900 ppm</strong>, the highest level of organic fluorine
        the testing lab had ever found in a consumer product. Target&apos;s
        Up &amp; Up Smooth Slide, Colgate Total Mint Waxed, and Amazon Solimo
        each tested over 70,000 ppm. PFAS is added intentionally to create
        the &ldquo;slide&rdquo; effect. Switch to silk-based or PFAS-free
        polyester floss. Cocofloss, Dr. Tung&apos;s Smart Floss (silk), Lucky
        Teeth, Radius Vegan, and most kids&apos; floss tested PFAS-free.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>The 2024 Mamavation study (EPA-certified lab) found PFAS in ~33% of 39 floss products tested.</>,
          <><strong>Oral-B Glide</strong> contained 248,900 ppm of organic fluorine, the highest level the testing lab had ever recorded in a consumer product.</>,
          <>Target&apos;s Up &amp; Up Smooth Slide, Colgate Total Mint Waxed, and Amazon Solimo Smooth Slide each tested over 70,000 ppm.</>,
          <>PFAS is added intentionally to create the &ldquo;slide&rdquo; or &ldquo;glide&rdquo; texture between teeth.</>,
          <>Floss is used daily in direct contact with gum tissue, chemical absorption is plausible and the FDA has expressed concern.</>,
          <>Safer floss: silk (Dr. Tung&apos;s Smart Floss), Cocofloss, Lucky Teeth, Radius Vegan, and most children&apos;s floss products.</>,
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Why PFAS is in dental floss
      </h2>
      <p>
        PFAS (per- and polyfluoroalkyl substances), the &ldquo;forever
        chemicals&rdquo;, are added to certain dental floss products to give
        them a smooth, slippery texture that helps them glide between tight
        teeth without fraying. The same chemistry is used in non-stick cookware
        coatings, water-resistant clothing, and food packaging.
      </p>
      <p>
        The catch: PFAS don&apos;t stay on the floss. They migrate into gum
        tissue with every use. A 2019 Silent Spring Institute study published
        in the <em>Journal of Exposure Science &amp; Environmental
        Epidemiology</em> found women who used PFAS-treated floss had higher
        serum PFAS levels than non-users.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The 2024 Mamavation testing
      </h2>
      <p>
        Mamavation commissioned an EPA-certified laboratory to test 39 dental
        floss products for organic fluorine (a PFAS indicator). Headline
        findings:
      </p>

      <BlogComparisonTable
        caption="Mamavation 2024 dental floss PFAS testing, highest detected"
        columns={[
          { key: 'brand', label: 'Product' },
          { key: 'ppm', label: 'Organic fluorine (ppm)' },
          { key: 'risk', label: 'Recommendation' },
        ]}
        rows={[
          { brand: 'Oral-B Glide', ppm: '248,900', risk: 'Avoid, highest ever recorded in a consumer product' },
          { brand: 'Up & Up Smooth Slide (Target)', ppm: '~70,000+', risk: 'Avoid' },
          { brand: 'Colgate Total Mint Waxed', ppm: '~70,000+', risk: 'Avoid' },
          { brand: 'Solimo Extra Comfort Mint (Amazon)', ppm: '~70,000+', risk: 'Avoid' },
          { brand: 'Other "smooth" / "glide" branded floss', ppm: '11-50,000', risk: 'Check Mamavation full list' },
          { brand: 'Most kids\' floss products tested', ppm: 'Not detected', risk: 'Likely PFAS-free' },
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What PFAS does to the body
      </h2>
      <p>
        PFAS chemicals don&apos;t break down in the environment or in the human
        body, hence &ldquo;forever chemicals&rdquo;. EPA, EFSA, and ATSDR
        all classify them as concerning. Documented and probable health effects:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li>Decreased fertility and reduced sperm quality</li>
        <li>Developmental effects in children (low birth weight)</li>
        <li>Reduced immune response (including reduced vaccine efficacy)</li>
        <li>Hormonal disruption, thyroid in particular</li>
        <li>Increased cholesterol and cardiovascular risk</li>
        <li>Some PFAS classified as probable human carcinogens (PFOA: IARC Group 2B)</li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Recommended PFAS-free floss brands
      </h2>

      <BlogComparisonTable
        caption="PFAS-free dental floss brands"
        columns={[
          { key: 'brand', label: 'Brand' },
          { key: 'material', label: 'Material' },
          { key: 'features', label: 'Features' },
          { key: 'price', label: 'Price' },
        ]}
        rows={[
          { brand: 'Dr. Tung\'s Smart Floss', material: 'Silk + plant wax', features: 'Stretchy; cleans wider area; biodegradable', price: '$5 / 30 yd' },
          { brand: 'Cocofloss', material: 'Polyester + coconut oil coating', features: 'No PFAS; cleans well; popular with dentists', price: '$10 / 32 yd' },
          { brand: 'Lucky Teeth Bamboo Charcoal Floss', material: 'Bamboo charcoal-infused polyester', features: 'PFAS-free; biodegradable container', price: '$10 / 30 yd' },
          { brand: 'Radius Vegan Floss', material: 'Plant-based; cellulose', features: 'PFAS-free; biodegradable', price: '$5 / 55 yd' },
          { brand: 'GuruNanda Vegan Floss', material: 'PLA-based', features: 'PFAS-free; biodegradable bioplastic', price: '$6 / 50 yd' },
          { brand: 'Tom\'s of Maine Naturally Waxed Floss', material: 'Nylon + natural wax', features: 'PFAS-free per Mamavation', price: '$3 / 35 yd' },
          { brand: 'Most kids\' floss products', material: 'Various', features: 'Most tested PFAS-free in the Mamavation study', price: 'Varies' },
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        How to switch and avoid PFAS in oral care
      </h2>
      <ol className="list-decimal pl-6 space-y-2">
        <li><strong>Discard any &ldquo;Glide&rdquo;, &ldquo;Smooth Slide&rdquo;, or &ldquo;ProGlide&rdquo; floss</strong> you have at home.</li>
        <li><strong>Switch to silk or PFAS-free polyester floss</strong> from the list above.</li>
        <li><strong>Check toothpaste for synthetic polymers and PFAS</strong>, see our{' '}
          <Link href="/blog/best-microplastic-free-toothpaste" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
            best polymer-free toothpaste guide
          </Link>.
        </li>
        <li><strong>Avoid &ldquo;stain repellent&rdquo; oral care products</strong>, same PFAS chemistry.</li>
        <li><strong>Consider a water flosser</strong> (Waterpik) as a PFAS-free alternative for some users.</li>
      </ol>

      <p>
        See related:{' '}
        <Link href="/blog/best-microplastic-free-toothpaste" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          best microplastic-free toothpaste
        </Link>,{' '}
        <Link href="/blog/best-microplastic-free-toothpaste" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics in toothpaste
        </Link>, and{' '}
        <Link href="/blog/microplastics-vs-pfas-vs-bpa" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics vs PFAS
        </Link>.
      </p>


      <BlogWhatTheAppChecks
        items={[
          <>Ingredient list parsed from the product label or barcode.</>,
          <>Flagged ingredients, polyethylene, acrylates, carbomer, PEG, fragrance, parabens.</>,
          <>Product category, leave-on vs rinse-off; risk weighted differently.</>,
          <>Brand and product line, clean certifications (EWG VERIFIED, MADE SAFE).</>,
          <>Cited research and regulatory references for each scan.</>,
        ]}
      />

      <BlogAppCTA
        headline="Scan personal-care products before buying"
        sub="The MicroPlastics app reads the ingredient list, flags microplastic polymers and additives, and points to cleaner alternatives in the same category."
        buttonLabel="Scan cosmetics in the app"
      />

      <BlogFAQ
        items={[
          {
            question: 'Does dental floss contain PFAS?',
            answer:
              'A 2024 Mamavation study using an EPA-certified lab tested 39 floss products and found PFAS in roughly one third. Oral-B Glide contained 248,900 ppm of organic fluorine, the highest the testing lab had ever recorded in any consumer product. Several other "glide" and "smooth slide" brands also tested high.',
          },
          {
            question: 'Is Oral-B Glide bad for you?',
            answer:
              'Oral-B Glide tested at 248,900 ppm of organic fluorine in the 2024 Mamavation lab analysis, the highest ever recorded in a consumer product. PFAS chemicals migrate into gum tissue during use and accumulate in the body. Many dentists and toxicologists recommend switching.',
          },
          {
            question: 'What is the safest dental floss brand?',
            answer:
              'Dr. Tung\'s Smart Floss (silk), Cocofloss, Lucky Teeth Bamboo Charcoal, Radius Vegan, and Tom\'s of Maine Naturally Waxed all tested PFAS-free. Most children\'s floss products also tested PFAS-free in the Mamavation study.',
          },
          {
            question: 'Is silk dental floss better?',
            answer:
              'Yes for chemical safety. Silk floss (Dr. Tung\'s Smart Floss) is naturally biodegradable, doesn\'t require PFAS coating to slide, and stretches to clean a wider area than synthetic floss. Cost is similar to premium synthetic floss.',
          },
          {
            question: 'Are water flossers a good alternative?',
            answer:
              'Yes. Water flossers (Waterpik, others) eliminate PFAS exposure entirely. Multiple studies show they\'re effective at removing plaque between teeth, especially for braces, implants, and tight contacts. They don\'t fully replace string flossing for everyone but are a strong PFAS-free option.',
          },
          {
            question: 'How does PFAS in floss affect the body?',
            answer:
              'PFAS chemicals migrate from floss into gum tissue during use. A 2019 Silent Spring Institute study found women who used PFAS-treated floss had higher serum PFAS levels. Chronic PFAS exposure is linked to reduced fertility, thyroid disruption, immune effects, and elevated cholesterol.',
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'Mamavation',
            year: 2024,
            title: 'Toxic PFAS in Dental Floss & Tooth Floss',
            publisher: 'Mamavation (EPA-certified lab testing)',
            url: 'https://mamavation.com/beauty/toxic-pfas-dental-floss-tooth-floss.html',
          },
          {
            authors: 'Boronow KE, Brody JG, Schaider LA, et al.',
            year: 2019,
            title: 'Serum concentrations of PFASs and exposure-related behaviors in African American and non-Hispanic white women',
            publisher: 'Journal of Exposure Science & Environmental Epidemiology',
            url: 'https://www.nature.com/articles/s41370-018-0109-y',
          },
          {
            authors: 'US Environmental Protection Agency',
            year: 2024,
            title: 'PFOA, PFOS and Other PFAS: health effects',
            publisher: 'EPA',
            url: 'https://www.epa.gov/pfas/our-current-understanding-human-health-and-environmental-risks-pfas',
          },
          {
            authors: 'European Food Safety Authority',
            year: 2020,
            title: 'Risk to human health related to the presence of perfluoroalkyl substances in food',
            publisher: 'EFSA Journal',
            url: 'https://efsa.onlinelibrary.wiley.com/doi/10.2903/j.efsa.2020.6223',
          },
        ]}
      />
    </BlogPostLayout>
  )
}
